/**
 * Token Manager - Manejo seguro de tokens de autenticación
 * Implementa la estrategia de dual-token (access token + refresh token)
 */
import jwtDecode from 'jwt-decode';
import { TokenPair, TokenManager } from 'types/tokenAuth';
import { MUTATION_REFRESH_TOKENS } from 'graphql/mutations/auth';
import { client } from 'graphql/config';
import { IUserDataToken } from './types';
import { refreshCoordinator } from './refreshCoordinator';

class TokenManagerImpl implements TokenManager {
    private accessToken: string | null = null;

    // El refresh token se maneja a través de cookies HTTP-only
    // No se almacena en memoria por seguridad
    private tokenExpiry: number | null = null;

    private refreshPromise: Promise<TokenPair> | null = null;

    constructor() {
        this.initializeTokens();
    }

    /**
     * Inicializa los tokens desde el almacenamiento seguro
     */
    private initializeTokens(): void {
        // El refresh token se maneja a través de HTTP-only cookies en el backend
        // El access token se mantiene en memoria
        const backend_jwt = localStorage.getItem('backend_jwt');

        this.accessToken = backend_jwt;
        this.tokenExpiry = this.generateTokenExpiry(backend_jwt);
    }

    public generateTokenExpiry(token: string | null): number | null {
        try {
            if (!token) return null;

            const payload: IUserDataToken = jwtDecode(token);
            if (payload) {
                this.tokenExpiry = payload.exp;
                return payload.exp;
            }

            this.tokenExpiry = null;
            console.warn('Could not extract the exp field from the accessToken JWT');
            return null;
        } catch (e) {
            this.tokenExpiry = null;
            console.error('Error decodificando accessToken para obtener exp:', e);
            return null;
        }
    }

    /**
     * Establece un nuevo par de tokens
     * El refresh token se maneja automáticamente por cookies HTTP-only
     */
    public setTokens(tokenPair: TokenPair): void {
        this.applyNewAccessToken(tokenPair.accessToken);
        this.refreshPromise = null;
    }

    private applyNewAccessToken(accessToken: string): void {
        this.accessToken = accessToken;
        localStorage.setItem('backend_jwt', accessToken);
        this.tokenExpiry = this.generateTokenExpiry(accessToken);
    }

    /**
     * Obtiene el access token actual
     */
    public getAccessToken(): string | null {
        return this.accessToken;
    }

    /**
     * Verifica si el access token está expirado
     */
    public isTokenExpired(): boolean {
        // Verifica si el 'exp' del accessToken (en segundos desde Epoch) es menor al tiempo actual
        if (!this.accessToken || !this.tokenExpiry) {
            return true;
        }
        // 'exp' viene en segundos desde Epoch, Date.now() es en milisegundos, hay que convertir
        const nowInSeconds = Math.floor(Date.now() / 1000);
        return nowInSeconds >= this.tokenExpiry;
    }

    /**
     * Renueva el access token usando el refresh token.
     * Deduplica peticiones dentro del mismo tab y coordina con otros tabs via BroadcastChannel
     * para que solo un tab ejecute la rotación a la vez.
     */
    public async refreshAccessToken(): Promise<string> {
        // Same-tab dedup: if we're already refreshing in this tab, wait on that promise.
        if (this.refreshPromise) {
            const result = await this.refreshPromise;
            return result.accessToken;
        }

        // Cross-tab coordination: if another tab already holds the refresh lock,
        // wait for its broadcast result instead of firing a competing rotation.
        if (refreshCoordinator.isAnotherTabRefreshing()) {
            try {
                const msg = await refreshCoordinator.waitForBroadcast();
                if (msg.accessToken) {
                    this.applyNewAccessToken(msg.accessToken);
                    return msg.accessToken;
                }
                // Broadcast arrived but no accessToken (cookie-only refresh path) —
                // fall through to refresh independently.
            } catch {
                // Timeout or failure in the other tab — fall through and try ourselves.
            }
        }

        refreshCoordinator.acquireLock();
        this.refreshPromise = this.performTokenRefresh();
        try {
            const result = await this.refreshPromise;
            refreshCoordinator.broadcastDone(result.accessToken);
            return result.accessToken;
        } catch (err) {
            refreshCoordinator.broadcastFailed((err as Error)?.message ?? 'Refresh failed');
            throw err;
        } finally {
            this.refreshPromise = null;
            refreshCoordinator.releaseLock();
        }
    }

    // eslint-disable-next-line class-methods-use-this
    private getRefreshTokenFromCookie(): string | null {
        const cookies = document.cookie.split(';');
        const match = cookies.find((c) => c.trim().startsWith('refreshToken='));
        return match ? match.split('=')[1] : null;
    }

    /**
     * Realiza la renovación del token usando GraphQL
     * El refresh token se obtiene de las cookies HTTP-only
     */
    private async performTokenRefresh(): Promise<TokenPair> {
        try {
            // Obtener refresh token de las cookies HTTP-only
            const refreshToken = this.getRefreshTokenFromCookie();

            if (!refreshToken) {
                throw new Error('No refresh token available in cookies');
            }

            const { data } = await client.mutate({
                mutation: MUTATION_REFRESH_TOKENS,
                variables: {
                    refreshToken
                }
            });

            if (!data?.refreshTokens) {
                throw new Error('Invalid refresh response');
            }

            const { accessToken, refreshToken: newRefreshToken } = data.refreshTokens;

            // Actualizar tokens (el refresh token se rota)
            this.setTokens({
                accessToken,
                refreshToken: newRefreshToken
            });

            return {
                accessToken,
                refreshToken: newRefreshToken
            };
        } catch (error) {
            // console.log('error perf', error);
            // Limpiar tokens en caso de error
            this.clearTokens();
            throw error;
        }
    }

    /**
     * Obtiene un token válido (renovando si es necesario)
     */
    public async getValidToken(): Promise<string> {
        if (!this.accessToken || this.isTokenExpired()) {
            return this.refreshAccessToken();
        }
        return this.accessToken;
    }

    public clearTokens(): void {
        this.accessToken = null;

        localStorage.removeItem('backend_jwt');
        // Temporal: limpiar refresh token de localStorage
        localStorage.removeItem('refreshToken');

        // El refresh token se limpia automáticamente por el backend
        // cuando se elimina la cookie HTTP-only

        this.tokenExpiry = null;
        this.refreshPromise = null;
        // Remove any legacy refresh token that may have been written by older code.
        localStorage.removeItem('refreshToken');
    }

    /**
     * Verifica si el usuario está autenticado
     */
    public isAuthenticated(): boolean {
        return this.accessToken !== null;
    }
}

// Instancia singleton del TokenManager
export const tokenManager = new TokenManagerImpl();
export default tokenManager;
